import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Linkedin, Printer, ArrowUpRight } from 'lucide-react';
import { mockData } from '../mock';

const SOCIAL_LINKS = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/share/1B24hhrchJ/',
    Icon: Facebook,
    testId: 'social-facebook',
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/in/moveracare-mobility-87b937408',
    Icon: Linkedin,
    testId: 'social-linkedin',
  },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const getComplianceIcon = (item) => {
    const icons = {
      "Licensed & Insured": "🛡️",
      "HIPAA Conscious Operations": "🔒",
      "Wheelchair-Accessible Vehicles": "♿",
      "MBE Certified": "⭐"
    };
    return icons[item] || "✓";
  };

  return (
    <footer className="bg-[#004C97] text-white">
      {/* Compliance Bar */}
      <div className="bg-[#F9A825]/10 border-y border-[#F9A825]/20 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {mockData.complianceItems.map((item) => (
              <div key={item} className="flex items-center space-x-2">
                <span className="text-[#F9A825] text-lg flex-shrink-0">{getComplianceIcon(item)}</span>
                <span className="text-xs text-white">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src="/logo.png" 
              alt="MoveraCare Mobility" 
              className="h-20 w-auto mb-4 bg-white p-3 rounded-lg shadow-sm"
            />
            <p className="text-gray-300 text-sm mb-4">
              Professional non-emergency medical transportation services across Greater Houston. Reliable, safe, and on-time.
            </p>
            <div className="space-y-2">
              <a href={"tel:+18329252128"} className="flex items-center space-x-2 text-sm text-gray-300 hover:text-[#F9A825] transition-colors">
                <Phone className="w-4 h-4" />
                <span>{mockData.company.phone}</span>
              </a>
              <div className="flex items-center space-x-2 text-sm text-gray-300" data-testid="footer-fax">
                <Printer className="w-4 h-4" />
                <span>Fax: {mockData.company.fax}</span>
              </div>
              <a href={`mailto:${mockData.company.email}`} className="flex items-center space-x-2 text-sm text-gray-300 hover:text-[#F9A825] transition-colors">
                <Mail className="w-4 h-4" />
                <span>{mockData.company.email}</span>
              </a>
              <div className="flex items-center space-x-2 text-sm text-gray-300">
                <MapPin className="w-4 h-4" />
                <span>{mockData.company.address}</span>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-5">
              <p className="text-xs uppercase tracking-wider text-[#F9A825] font-semibold mb-2">Follow Us</p>
              <div className="flex items-center space-x-2" data-testid="footer-social-links">
                {SOCIAL_LINKS.map(({ name, href, Icon, testId }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`MoveraCare Mobility on ${name}`}
                    data-testid={testId}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#F9A825] hover:text-[#004C97] transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Transportation Services — direct links to all 7 customer-facing
              service pages, present in the footer of every page. Gives
              Discovered-not-indexed service URLs many high-signal internal
              inlinks and gives users one-click access from anywhere. */}
          <div>
            <h3 className="text-[#F9A825] font-semibold mb-4">Transportation Services</h3>
            <ul className="space-y-2">
              <li><Link to="/services/wheelchair-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-service-wheelchair">Wheelchair Transportation</Link></li>
              <li><Link to="/services/ambulatory-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-service-ambulatory">Ambulatory Transportation</Link></li>
              <li><Link to="/services/dialysis-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-service-dialysis">Dialysis Transportation</Link></li>
              <li><Link to="/services/hospital-discharge" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-service-hospital-discharge">Hospital Discharge Transportation</Link></li>
              <li><Link to="/services/therapy-rehabilitation-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-service-therapy">Therapy &amp; Rehabilitation Rides</Link></li>
              <li><Link to="/services/recurring-medical-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-service-recurring">Recurring Medical Rides</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-service-all">All Services</Link></li>
            </ul>
          </div>

          {/* Provider Resources */}
          <div>
            <h3 className="text-[#F9A825] font-semibold mb-4">Provider Resources</h3>
            <ul className="space-y-2">
              <li><Link to="/healthcare-partners/workers-compensation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Workers&apos; Compensation</Link></li>
              <li><Link to="/healthcare-partners/healthcare-facilities" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Healthcare Facilities</Link></li>
              <li><Link to="/healthcare-partners/insurance-tpa" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Insurance &amp; TPAs</Link></li>
              <li><Link to="/healthcare-partners/nemt-brokers" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">NEMT Brokers</Link></li>
              <li><Link to="/healthcare-partners/dialysis-centers" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Dialysis Centers</Link></li>
              <li><Link to="/healthcare-partners/hospital-discharge" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Hospital Discharge</Link></li>
              <li><a href="/MoveraCare-Vendor-Procurement-Packet.pdf" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Vendor Packet (PDF)</a></li>
              <li><a href="mailto:dispatch@moveracaremobility.com" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Provider Relations</a></li>
            </ul>
          </div>

          {/* Service Areas - SEO Location Pages */}
          <div>
            <h3 className="text-[#F9A825] font-semibold mb-4">Service Areas</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/houston-tx-medical-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm" data-testid="footer-link-houston">
                  Houston TX Transportation
                </Link>
              </li>
              <li>
                <Link to="/richmond-tx-medical-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">
                  Richmond TX Transportation
                </Link>
              </li>
              <li>
                <Link to="/sugar-land-tx-medical-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">
                  Sugar Land TX NEMT
                </Link>
              </li>
              <li>
                <Link to="/katy-tx-medical-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">
                  Katy TX Transport
                </Link>
              </li>
              <li>
                <Link to="/missouri-city-tx-medical-transportation" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">
                  Missouri City TX
                </Link>
              </li>
            </ul>
          </div>

          {/* Operating Hours */}
          <div>
            <h3 className="text-[#F9A825] font-semibold mb-4">Operating Hours</h3>
            <p className="text-gray-300 text-sm mb-4">{mockData.company.hours}</p>
            <h4 className="text-white font-semibold mb-2">Dispatch</h4>
            <a href={"tel:+18329252128"} className="text-[#F9A825] hover:text-[#E08E1E] transition-colors font-semibold">
              {mockData.company.dispatchPhone}
            </a>
          </div>
        </div>
      </div>

      {/* MBE Certified Strip — premium visibility */}
      <div className="bg-white border-y-4 border-[#F9A825] py-6" data-testid="mbe-certified-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-5 text-center md:text-left">
            <div className="flex items-center gap-5">
              <img
                src="/mbe-certified.webp"
                alt="City of Houston Certified Minority Business Enterprise"
                className="h-20 w-20 md:h-24 md:w-24 flex-shrink-0 object-contain"
                data-testid="mbe-certified-logo"
                loading="lazy"
              />
              <div>
                <p className="text-xs uppercase tracking-widest text-[#F9A825] font-bold mb-1">
                  City of Houston Certified
                </p>
                <p className="text-xl md:text-2xl font-extrabold text-[#004C97] leading-tight">
                  Minority Business Enterprise (MBE)
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 font-medium max-w-md md:text-right leading-relaxed">
              Officially certified by the City of Houston — committed to diversity, inclusion, and excellence in healthcare transportation.
            </p>
          </div>
        </div>
      </div>

      {/* Network Partner Strip — Prime Health Services */}
      <div className="bg-[#F8FAFC] border-b-4 border-[#004C97] py-6" data-testid="network-partner-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-5 text-center md:text-left">
            <div className="flex items-center gap-5">
              <div className="bg-white rounded-lg p-2 shadow-sm flex-shrink-0">
                <img
                  src="/prime-health.png"
                  alt="Prime Health Services"
                  className="h-16 w-16 md:h-20 md:w-20 object-contain"
                  data-testid="prime-health-logo"
                  loading="lazy"
                />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-[#F9A825] font-bold mb-1">
                  Network Partner
                </p>
                <p className="text-xl md:text-2xl font-extrabold text-[#004C97] leading-tight">
                  In Network with Prime Health Services
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 font-medium max-w-md md:text-right leading-relaxed">
              Approved transportation provider for Prime Health Services — streamlined billing and authorizations for covered patients.
            </p>
          </div>
        </div>
      </div>

      {/* Community Sponsorship — reduced hierarchy, placed below Prime Health */}
      <div className="bg-[#FAFAF7] py-6 border-t border-gray-100" data-testid="official-sponsor-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href="https://daplittlevogue.com"
            target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label="Community sponsor: Daplittlevogue"
            data-testid="official-sponsor-link"
            className="group flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            <span className="text-[10px] sm:text-xs font-semibold text-[#F9A825] uppercase" style={{ letterSpacing: '0.25em' }}>
              Community Sponsor
            </span>
            <span className="hidden sm:inline text-gray-300">·</span>
            <img
              src="/daplittlevogue-logo.webp"
              alt="Daplittlevogue"
              className="h-8 w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity"
              data-testid="official-sponsor-logo"
              loading="lazy"
            />
            <span className="text-sm text-gray-700 group-hover:text-[#004C97] transition-colors">
              Proud Community Sponsor: <span className="font-semibold text-[#004C97]">Daplittlevogue</span>
              <ArrowUpRight className="inline-block ml-1 w-3 h-3" aria-hidden="true" />
            </span>
          </a>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-300 text-sm">
              © {currentYear} MoveraCare Mobility. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">Terms of Service</Link>
              <Link to="/hipaa" className="text-gray-300 hover:text-[#F9A825] transition-colors text-sm">HIPAA Notice</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
